import { create } from 'zustand';
import type {
  ActivityFeedEntry,
  Agent,
  Artist,
  Booking,
  BookingFlag,
  ConversationEntry,
  CustomerRecord,
  Deposit,
  DesignIntent,
  FutureReadinessSignal,
  MedicalProfile,
  StudioId,
  TattooStyle,
  Piece,
  SelfBookLink,
  SessionPhotoKind,
  Studio,
  TailRecord,
  AIConversation,
  ReactivationCampaign,
  MarketingCampaign,
  ConfirmationCall,
  InternalCommsChannel,
  InternalCommsMessage,
  InternalDmThread,
  RebookAttribution,
  RebookFollowupTask,
  HealingCheckTask,
  NoRebookFlag,
  NoDepositDeclineFlag,
  RebookOutcome,
  OverrideRequest,
  RescheduleReason,
  Waiver,
  SalePayment,
  PayoutRecord,
  CashLedger,
  MerchItem,
  DocketLine,
  SettlementLanding,
  DepositLanding,
  SettlementLump,
  DayPack,
  TopupPlan,
} from '@/domain/types';
import { DEFAULT_ROLE_PERMISSIONS, type RolePermission } from '@/domain/permissions';
import {
  DEFAULT_REPORTING_TARGETS,
  type ReportingTargetsConfig,
  type ArtistWeeklyTarget,
} from '@/data/reportingTargets';
import type { SequenceState } from '@/domain/sequenceFlow';
import type { ReconResolution } from '@/domain/reconResolutionFlow';
import { seedNow, seedSilent } from '@/domain/seedContext';
import { useSimulationStore } from '@/state/simulationStore';
import { stableHash } from '@/utils/seededRandom';
import { fixtureBundleToState } from '@/data/fixtureBundleToState';
import { deriveTodayStats } from '@/data/todayStats';

export interface BrainState {
  initialised: boolean;
  customers: Record<string, CustomerRecord>;
  customerIdsByPhone: Record<string, string>;
  bookings: Record<string, Booking>;
  deposits: Record<string, Deposit>;
  /** Agent-built pay-over-time deposit plans (tv-w98); keyed by plan id. */
  topupPlans: Record<string, TopupPlan>;
  waivers: Record<string, Waiver>;
  pieces: Record<string, Piece>;
  futureSignals: Record<string, FutureReadinessSignal>;
  selfBookLinks: Record<string, SelfBookLink>;
  aiConversations: Record<string, AIConversation>;
  reactivationCampaigns: Record<string, ReactivationCampaign>;
  /** Marketing spend sources (Prompt F): funnel ad-spend + per-campaign cost-per-deposit. */
  marketingCampaigns: Record<string, MarketingCampaign>;
  confirmationCalls: Record<string, ConfirmationCall>;
  internalComms: Record<string, InternalCommsChannel>;
  internalDmThreads: Record<string, InternalDmThread>;
  rebookFollowupTasks: Record<string, RebookFollowupTask>;
  healingCheckTasks: Record<string, HealingCheckTask>;
  /** No-rebook mid-piece Ops flags (tv-3gl); keyed by flag id. Read by tv-v4p. */
  noRebookFlags: Record<string, NoRebookFlag>;
  /** Declined no-deposit override flags (tv-bvx); keyed `ndf-${customerId}`. A2 writes, C2 reads/resolves. */
  noDepositDeclineFlags: Record<string, NoDepositDeclineFlag>;
  overrideRequests: Record<string, OverrideRequest>;
  /** Reconcile exception resolutions (K1): resolver note + attachment + approval trail, keyed by row id. */
  reconResolutions: Record<string, ReconResolution>;
  sequenceState: Record<string, SequenceState>;
  /** Make-sale records written at checkout (E1); read by E3 reconciliation. */
  salePayments: Record<string, SalePayment>;
  /** Signed cash-envelope handovers (E2); keyed by record id. */
  payoutRecords: Record<string, PayoutRecord>;
  /** Per store, per day float/cash ledger (E2); keyed `${studioId}:${dayIso}`. */
  cashLedgers: Record<string, CashLedger>;
  /** Per-studio merch/aftercare catalogue (E1); keyed by item id. */
  merchItems: Record<string, MerchItem>;
  /** Terminal / deposit-sheet reference echoes (E3); read by reconciliation. */
  docketLines: Record<string, DocketLine>;
  /** Card-takings settlement landings (E3 cash-timing strip); keyed by id. */
  settlementLandings: Record<string, SettlementLanding>;
  /** Deposit bank landings (amendment 11 deposit-recon lane); keyed by id. */
  depositLandings: Record<string, DepositLanding>;
  /** Bank settlement lumps (E4); split by reference. Keyed by id. */
  settlementLumps: Record<string, SettlementLump>;
  /** Frozen end-of-day packs (E4); keyed `${studioId}:${dayIso}`. */
  dayPacks: Record<string, DayPack>;
  studios: Record<string, Studio>;
  artists: Record<string, Artist>;
  agents: Record<string, Agent>;
  rolePermissions: RolePermission[];
  /** Editable weekly targets/thresholds (Settings Targets tab). Config, not fixture data. */
  reportingTargets: ReportingTargetsConfig;
  tailRecords: TailRecord[];
  activityFeed: ActivityFeedEntry[];
  todayStats: {
    deposits: number;
    callsRecovered: number;
    crossStudioRoutes: number;
    inChairRebookings: number;
    newCustomers: number;
    networkRevenueAud: number;
  };

  loadFixtures: () => void;
  /** Re-derive `todayStats` from the current records (run after the journey replay lands). */
  recomputeTodayStats: () => void;
  upsertCustomer: (c: CustomerRecord) => void;
  /**
   * Persist a customer's medical profile. The store owns the audit stamp —
   * callers pass the clinical fields only; `lastUpdatedAt` / `lastUpdatedBy`
   * are set here (server-side in production, in-store for the prototype).
   */
  updateMedicalProfile: (
    customerId: string,
    profile: Omit<MedicalProfile, 'lastUpdatedAt' | 'lastUpdatedBy'>,
    updatedBy?: string,
  ) => void;
  /**
   * Shallow-merge preference keys onto a customer (persist). No-op if the
   * customer is missing. Later scopes artist filtering.
   */
  setCustomerPreferences: (
    customerId: string,
    patch: {
      preferredArtistId?: string;
      preferredStyle?: TattooStyle;
      preferredStudioId?: StudioId;
    },
  ) => void;
  /**
   * Merge a patch onto a customer's designIntent (the call brief), creating a
   * minimal object if none exists. No-op if the customer is missing.
   */
  updateDesignIntent: (customerId: string, patch: Partial<DesignIntent>) => void;
  addConversationEntry: (customerId: string, entry: ConversationEntry) => void;
  addRapportNote: (customerId: string, note: string, author?: string, atIso?: string) => void;
  addFutureReadinessSignal: (signal: FutureReadinessSignal) => void;
  upsertBooking: (b: Booking) => void;
  /**
   * Keep-original reschedule (15 Jul ruling), applied atomically in one `set`.
   * The original is preserved as `superseded` and a NEW booking is created for
   * the new slot, linked both ways; the paid deposit is repointed to the new
   * record. Orchestrated by `rescheduleBooking` in src/domain/rescheduleFlow.ts.
   */
  applyReschedule: (
    args: {
      originalId: string;
      reason: RescheduleReason;
      note?: string;
      by: string;
      at: string;
    } & (
      | { mode: 'move'; newBookingId: string; artistId: string; newStartIso: string }
      | { mode: 'release' }
    ),
  ) => void;
  /** Persist an agent-dismissed flag *suggestion* (derived flags aren't stored). */
  dismissFlagSuggestion: (bookingId: string, kind: BookingFlag) => void;
  upsertArtist: (a: Artist) => void;
  upsertStudio: (s: Studio) => void;
  upsertAgent: (a: Agent) => void;
  setRolePermissions: (next: RolePermission[]) => void;
  /** Patch one artist's weekly targets (merges over the current resolved target). */
  setArtistTarget: (artistId: string, patch: Partial<ArtistWeeklyTarget>) => void;
  /** Set a studio's weekly reviews target. */
  setStudioReviewsTarget: (studioId: string, reviewsPerWeek: number) => void;
  upsertDeposit: (d: Deposit) => void;
  /** Upsert an instalment (top-up) plan, keyed by plan id (tv-w98). */
  upsertTopupPlan: (p: TopupPlan) => void;
  /** Upsert a waiver; registers its id on the owning customer's `waivers` array. */
  upsertWaiver: (w: Waiver) => void;
  upsertPiece: (p: Piece) => void;
  addPieceProgressPhoto: (
    pieceId: string,
    photo: {
      url: string;
      caption?: string;
      bookingId?: string;
      capturedAt?: string;
      kind?: SessionPhotoKind;
    },
  ) => void;
  upsertFutureSignal: (s: FutureReadinessSignal) => void;
  upsertSelfBookLink: (l: SelfBookLink) => void;
  upsertAIConversation: (c: AIConversation) => void;
  upsertReactivationCampaign: (c: ReactivationCampaign) => void;
  upsertConfirmationCall: (c: ConfirmationCall) => void;
  setReconResolution: (r: ReconResolution) => void;
  removeReconResolution: (rowId: string) => void;
  appendActivity: (a: ActivityFeedEntry) => void;
  incStat: (key: keyof BrainState['todayStats'], by?: number) => void;
  sendChannelMessage: (
    channelId: string,
    body: string,
    opts?: {
      authorId?: string;
      parentMessageId?: string;
      mentions?: string[];
      customerRefId?: string;
      timestamp?: string;
    },
  ) => void;
  sendDmMessage: (
    threadId: string,
    body: string,
    opts?: {
      authorId?: string;
      mentions?: string[];
      customerRefId?: string;
      timestamp?: string;
    },
  ) => void;
  openOrCreateDm: (participantIds: string[], creatorId: string, nowIso: string) => string;
  /** Create a new internal-comms channel and return its id. The creator is always a member. */
  createChannel: (
    name: string,
    memberIds: string[],
    creatorId: string,
    opts?: { description?: string; studioId?: StudioId },
  ) => string;
  addReaction: (
    scope: { kind: 'channel' | 'dm'; containerId: string },
    messageId: string,
    emoji: string,
    byPersonId: string,
  ) => void;
  addAutoPing: (
    channelId: string,
    body: string,
    opts?: { customerRefId?: string; timestamp?: string; mentions?: string[] },
  ) => void;
  /** Record the in-chair rebook conversation outcome on a completed booking. */
  setRebookOutcome: (
    bookingId: string,
    outcome: RebookOutcome,
    attribution: RebookAttribution,
  ) => void;
  /** Upsert a sales-team follow-up task created from Outcome C. */
  upsertRebookFollowup: (task: RebookFollowupTask) => void;
  upsertHealingCheck: (task: HealingCheckTask) => void;
  /** Upsert a no-rebook mid-piece Ops flag (tv-3gl); read by the Ops feeder (tv-v4p). */
  upsertNoRebookFlag: (flag: NoRebookFlag) => void;
  /** Upsert a declined no-deposit override flag (tv-bvx); A2 writes, C2 reads/resolves. */
  upsertNoDepositDeclineFlag: (flag: NoDepositDeclineFlag) => void;
  /** Upsert a no-deposit override request (created + resolved via approvals.ts). */
  upsertOverrideRequest: (request: OverrideRequest) => void;
  /** Upsert a make-sale record (E1 checkout). */
  upsertSalePayment: (payment: SalePayment) => void;
  /** Upsert a payout record (E2 sign-off). */
  upsertPayoutRecord: (record: PayoutRecord) => void;
  /** Upsert a day's cash ledger (E2), keyed `${studioId}:${dayIso}`. */
  upsertCashLedger: (ledger: CashLedger) => void;
  /** Upsert a merch/aftercare catalogue item (E1). */
  upsertMerchItem: (item: MerchItem) => void;
  /** Upsert a frozen day-close pack (E4); keyed `${studioId}:${dayIso}`. */
  upsertDayPack: (pack: DayPack) => void;
  /** Upsert a customer's sequence-clock state (replaces the existing entry). */
  setSequenceState: (customerId: string, state: SequenceState) => void;
  /** Shallow-merge a patch onto a customer's sequence state (creates stage 1 if none). */
  patchSequenceState: (customerId: string, patch: Partial<SequenceState>) => void;
  /** Remove a customer's sequence-clock state entirely. */
  clearSequenceState: (customerId: string) => void;
  /**
   * Mark a sales-team follow-up resolved. Side-effect: walks back to the
   * source booking and patches its rebookOutcome.resolvedOutcome.
   */
  resolveRebookFollowup: (
    taskId: string,
    resolution: { outcome: 'booked' | 'declined' | 'noContact'; resolvedAt: string },
  ) => void;
  reset: () => void;
}

/** "Now" for the app: the replay timestamp while seeding, else the sim clock. */
function nowIso(): string {
  return seedNow() ?? useSimulationStore.getState().clockIso;
}

/**
 * A progress photo's id. Derived, never random, so replaying the same capture
 * against the same sim clock mints the same id and snapshots compare equal.
 *
 * The identity of a photo is the piece it hangs off, the instant it was
 * captured, and what it actually IS (its url plus how it was filed). An artist
 * snapping 2-3 shots in one action stamps them all with the same `capturedAt`,
 * so the url/kind hash is what keeps them apart. `taken` is the last-resort
 * guard: if that tuple has already been filed on this piece (the same picture
 * captured twice at the same instant, or a 32-bit hash collision) the id gets an
 * ordinal, which still depends only on prior state and not on the wall clock.
 */
function progressPhotoId(
  pieceId: string,
  capturedAt: string,
  url: string,
  kind: SessionPhotoKind | undefined,
  taken: ReadonlySet<string>,
): string {
  // Readable stamp: yyyymmddHHmmss, offset dropped (the hash below carries it).
  const stamp = capturedAt.replace(/\D/g, '').slice(0, 14);
  const digest = stableHash(`${capturedAt}|${kind ?? 'progress'}|${url}`).toString(36);
  const base = `pp-${pieceId}-${stamp}-${digest}`;
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

const emptyStats = {
  deposits: 0,
  callsRecovered: 0,
  crossStudioRoutes: 0,
  inChairRebookings: 0,
  newCustomers: 0,
  networkRevenueAud: 0,
};

export const useBrainStore = create<BrainState>((set, get) => ({
  initialised: false,
  customers: {},
  customerIdsByPhone: {},
  bookings: {},
  deposits: {},
  topupPlans: {},
  waivers: {},
  pieces: {},
  futureSignals: {},
  selfBookLinks: {},
  aiConversations: {},
  reactivationCampaigns: {},
  marketingCampaigns: {},
  confirmationCalls: {},
  internalComms: {},
  internalDmThreads: {},
  rebookFollowupTasks: {},
  healingCheckTasks: {},
  noRebookFlags: {},
  noDepositDeclineFlags: {},
  overrideRequests: {},
  reconResolutions: {},
  sequenceState: {},
  salePayments: {},
  payoutRecords: {},
  cashLedgers: {},
  merchItems: {},
  docketLines: {},
  settlementLandings: {},
  depositLandings: {},
  settlementLumps: {},
  dayPacks: {},
  studios: {},
  artists: {},
  agents: {},
  rolePermissions: DEFAULT_ROLE_PERMISSIONS,
  reportingTargets: DEFAULT_REPORTING_TARGETS,
  tailRecords: [],
  activityFeed: [],
  todayStats: { ...emptyStats },

  loadFixtures: () => {
    // Phase-1 backend wiring: hydrate from GET /api/bootstrap (the api serves
    // the FixtureBundle — DB-backed collections from Postgres, the rest passed
    // through from the snapshot until their domain migrations land). Falls
    // back to the in-bundle fixtures when no backend is reachable, so the SPA
    // still runs standalone (vitest, story tests, `vite` alone).
    void (async () => {
      let f: import('@/data/fixtures').FixtureBundle;
      try {
        const res = await fetch('/api/bootstrap');
        if (!res.ok) throw new Error(`bootstrap ${res.status}`);
        f = (await res.json()) as import('@/data/fixtures').FixtureBundle;
        console.info('[boot] hydrated from /api/bootstrap');
      } catch {
        // Lazy-load fixtures to avoid pulling them into early bundles.
        const mod = await import('@/data/fixtures');
        f = mod.buildFixtures();
        console.info('[boot] backend unreachable — hydrated from local fixtures');
      }
      set(fixtureBundleToState(f));

      // Phase 2: replay the coherent journey fill on top of the static bundle.
      const rmod = await import('@/data/journeys/replayJourneys');
      const jmod = await import('@/data/journeys/journeyManifest');
      rmod.replayJourneys(jmod.JOURNEY_MANIFEST);
      get().recomputeTodayStats();
    })();
  },
  recomputeTodayStats: () =>
    set((s) => ({
      todayStats: deriveTodayStats({
        deposits: s.deposits,
        confirmationCalls: s.confirmationCalls,
        customers: s.customers,
        bookings: s.bookings,
        salePayments: s.salePayments,
        todayIso: useSimulationStore.getState().clockIso,
      }),
    })),
  upsertCustomer: (c) =>
    set((s) => ({
      customers: { ...s.customers, [c.id]: c },
      customerIdsByPhone: { ...s.customerIdsByPhone, [c.phone.replace(/\s+/g, '')]: c.id },
    })),
  updateMedicalProfile: (customerId, profile, updatedBy) =>
    set((s) => {
      const existing = s.customers[customerId];
      if (!existing) return s;
      const stamped: MedicalProfile = {
        ...profile,
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: updatedBy ?? 'System',
      };
      return {
        customers: {
          ...s.customers,
          [customerId]: { ...existing, medicalProfile: stamped },
        },
      };
    }),
  setCustomerPreferences: (customerId, patch) =>
    set((s) => {
      const existing = s.customers[customerId];
      if (!existing) return s;
      return {
        customers: {
          ...s.customers,
          [customerId]: { ...existing, ...patch },
        },
      };
    }),
  updateDesignIntent: (customerId, patch) =>
    set((s) => {
      const existing = s.customers[customerId];
      if (!existing) return s;
      const base: DesignIntent = existing.designIntent ?? {
        type: 'fine-line',
        placement: '',
        estimatedHours: 0,
        size: 'small',
        referencePhotos: [],
      };
      return {
        customers: {
          ...s.customers,
          [customerId]: { ...existing, designIntent: { ...base, ...patch } },
        },
      };
    }),
  addConversationEntry: (customerId, entry) =>
    set((s) => {
      const existing = s.customers[customerId];
      if (!existing) return s;
      return {
        customers: {
          ...s.customers,
          [customerId]: {
            ...existing,
            conversation: [...existing.conversation, entry],
          },
        },
      };
    }),
  addRapportNote: (customerId, note, author = 'AI', atIso) =>
    set((s) => {
      const existing = s.customers[customerId];
      if (!existing) return s;
      const rapportNote = {
        id: `rapport-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        note,
        author,
        timestamp: atIso ?? new Date().toISOString(),
      };
      return {
        customers: {
          ...s.customers,
          [customerId]: {
            ...existing,
            rapportNotes: [...existing.rapportNotes, rapportNote],
          },
        },
      };
    }),
  addFutureReadinessSignal: (sig) =>
    set((s) => {
      const existing = s.customers[sig.customerId];
      return {
        futureSignals: { ...s.futureSignals, [sig.id]: sig },
        customers: existing
          ? {
              ...s.customers,
              [sig.customerId]: {
                ...existing,
                futureReadinessSignals: [...existing.futureReadinessSignals, sig],
              },
            }
          : s.customers,
      };
    }),
  upsertBooking: (b) =>
    set((s) => {
      const existingCustomer = s.customers[b.customerId];
      const customers = existingCustomer && !existingCustomer.bookings.includes(b.id)
        ? {
            ...s.customers,
            [b.customerId]: {
              ...existingCustomer,
              bookings: [...existingCustomer.bookings, b.id],
            },
          }
        : s.customers;
      return { bookings: { ...s.bookings, [b.id]: b }, customers };
    }),
  applyReschedule: (args) =>
    set((s) => {
      const original = s.bookings[args.originalId];
      if (!original) return s;
      const toIso = args.mode === 'move' ? args.newStartIso : original.start;
      const historyEntry = {
        fromIso: original.start,
        toIso,
        reason: args.reason,
        note: args.note,
        by: args.by,
        at: args.at,
      };
      const history = [...(original.rescheduleHistory ?? []), historyEntry];
      const superseded: Booking = {
        ...original,
        status: 'superseded',
        rescheduleHistory: history,
        ...(args.mode === 'move' ? { rescheduledToBookingId: args.newBookingId } : {}),
      };
      const bookings = { ...s.bookings, [args.originalId]: superseded };
      let customers = s.customers;
      let deposits = s.deposits;

      if (args.mode === 'move') {
        // The new slot is a fresh booking; per-session lifecycle stamps do not
        // carry across (it has not been confirmed/checked-in/seated/completed).
        bookings[args.newBookingId] = {
          ...original,
          id: args.newBookingId,
          artistId: args.artistId,
          start: args.newStartIso,
          status: 'rescheduled',
          rescheduledFromBookingId: args.originalId,
          rescheduledToBookingId: undefined,
          rescheduleHistory: history,
          confirmedAt: undefined,
          checkedInAt: undefined,
          seatedAt: undefined,
          checkedOutAt: undefined,
          actualStart: undefined,
        };
        const cust = s.customers[original.customerId];
        if (cust && !cust.bookings.includes(args.newBookingId)) {
          customers = {
            ...s.customers,
            [original.customerId]: { ...cust, bookings: [...cust.bookings, args.newBookingId] },
          };
        }
        // Repoint the paid deposit from the superseded record to the new one so
        // it stays linked to a live dated booking (no double-count, no orphan).
        if (original.depositId) {
          const dep = s.deposits[original.depositId];
          if (dep && dep.bookingId === args.originalId) {
            deposits = { ...s.deposits, [dep.id]: { ...dep, bookingId: args.newBookingId } };
          }
        }
      } else if (original.depositId) {
        // release ("no new date yet"): unlink the paid deposit so the existing
        // deposit-on-file minting surfaces it in the money-cohort queue.
        const dep = s.deposits[original.depositId];
        if (dep && dep.status === 'paid' && dep.bookingId === args.originalId) {
          deposits = { ...s.deposits, [dep.id]: { ...dep, bookingId: undefined } };
        }
      }
      return { bookings, customers, deposits };
    }),
  dismissFlagSuggestion: (bookingId, kind) =>
    set((s) => {
      const existing = s.bookings[bookingId];
      if (!existing) return s;
      const current = existing.dismissedFlagSuggestions ?? [];
      if (current.includes(kind)) return s;
      return {
        bookings: {
          ...s.bookings,
          [bookingId]: { ...existing, dismissedFlagSuggestions: [...current, kind] },
        },
      };
    }),
  upsertArtist: (a) => set((s) => ({ artists: { ...s.artists, [a.id]: a } })),
  upsertStudio: (st) => set((s) => ({ studios: { ...s.studios, [st.id]: st } })),
  upsertAgent: (a) => set((s) => ({ agents: { ...s.agents, [a.id]: a } })),
  setRolePermissions: (next) => set({ rolePermissions: next }),
  setArtistTarget: (artistId, patch) =>
    set((s) => {
      const current = s.reportingTargets.artistOverrides[artistId] ?? s.reportingTargets.studioDefault;
      return {
        reportingTargets: {
          ...s.reportingTargets,
          artistOverrides: {
            ...s.reportingTargets.artistOverrides,
            [artistId]: { ...current, ...patch },
          },
        },
      };
    }),
  setStudioReviewsTarget: (studioId, reviewsPerWeek) =>
    set((s) => ({
      reportingTargets: {
        ...s.reportingTargets,
        reviewsByStudio: { ...s.reportingTargets.reviewsByStudio, [studioId]: reviewsPerWeek },
      },
    })),
  upsertDeposit: (d) => set((s) => ({ deposits: { ...s.deposits, [d.id]: d } })),
  upsertTopupPlan: (p) => set((s) => ({ topupPlans: { ...s.topupPlans, [p.id]: p } })),
  upsertWaiver: (w) =>
    set((s) => {
      const existingCustomer = s.customers[w.customerId];
      const alreadyLinked = existingCustomer?.waivers?.includes(w.id);
      const customers = existingCustomer && !alreadyLinked
        ? {
            ...s.customers,
            [w.customerId]: {
              ...existingCustomer,
              waivers: [...(existingCustomer.waivers ?? []), w.id],
            },
          }
        : s.customers;
      return { waivers: { ...s.waivers, [w.id]: w }, customers };
    }),
  upsertPiece: (p) => set((s) => ({ pieces: { ...s.pieces, [p.id]: p } })),
  addPieceProgressPhoto: (pieceId, photo) =>
    set((s) => {
      const existing = s.pieces[pieceId];
      if (!existing) return s;
      const capturedAt = photo.capturedAt ?? nowIso();
      const taken = new Set((existing.progressPhotos ?? []).map((p) => p.id));
      const entry = {
        id: progressPhotoId(pieceId, capturedAt, photo.url, photo.kind, taken),
        url: photo.url,
        capturedAt,
        bookingId: photo.bookingId,
        caption: photo.caption,
        kind: photo.kind,
      };
      return {
        pieces: {
          ...s.pieces,
          [pieceId]: {
            ...existing,
            progressPhotos: [...(existing.progressPhotos ?? []), entry],
          },
        },
      };
    }),
  upsertFutureSignal: (sig) =>
    set((s) => ({ futureSignals: { ...s.futureSignals, [sig.id]: sig } })),
  upsertSelfBookLink: (l) =>
    set((s) => ({ selfBookLinks: { ...s.selfBookLinks, [l.token]: l } })),
  upsertAIConversation: (c) =>
    set((s) => ({ aiConversations: { ...s.aiConversations, [c.id]: c } })),
  upsertReactivationCampaign: (c) =>
    set((s) => ({ reactivationCampaigns: { ...s.reactivationCampaigns, [c.id]: c } })),
  upsertConfirmationCall: (c) =>
    set((s) => ({ confirmationCalls: { ...s.confirmationCalls, [c.id]: c } })),
  setReconResolution: (r) =>
    set((s) => ({ reconResolutions: { ...s.reconResolutions, [r.rowId]: r } })),
  removeReconResolution: (rowId) =>
    set((s) => {
      const next = { ...s.reconResolutions };
      delete next[rowId];
      return { reconResolutions: next };
    }),
  appendActivity: (a) => {
    if (seedSilent()) return;
    set((s) => ({ activityFeed: [a, ...s.activityFeed].slice(0, 200) }));
  },
  incStat: (key, by = 1) => {
    if (seedSilent()) return;
    set((s) => ({ todayStats: { ...s.todayStats, [key]: s.todayStats[key] + by } }));
  },
  sendChannelMessage: (channelId, body, opts = {}) =>
    set((s) => {
      const channel = s.internalComms[channelId];
      if (!channel) return s;
      const msg: InternalCommsMessage = {
        id: `msg-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        authorId: opts.authorId ?? 'system-bot',
        body,
        timestamp: opts.timestamp ?? new Date().toISOString(),
        parentMessageId: opts.parentMessageId,
        mentions: opts.mentions,
        customerRefId: opts.customerRefId,
      };
      return {
        internalComms: {
          ...s.internalComms,
          [channelId]: { ...channel, messages: [...channel.messages, msg] },
        },
      };
    }),
  sendDmMessage: (threadId, body, opts = {}) =>
    set((s) => {
      const thread = s.internalDmThreads[threadId];
      if (!thread) return s;
      const msg: InternalCommsMessage = {
        id: `dm-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        authorId: opts.authorId ?? 'system-bot',
        body,
        timestamp: opts.timestamp ?? new Date().toISOString(),
        mentions: opts.mentions,
        customerRefId: opts.customerRefId,
      };
      return {
        internalDmThreads: {
          ...s.internalDmThreads,
          [threadId]: { ...thread, messages: [...thread.messages, msg] },
        },
      };
    }),
  openOrCreateDm: (participantIds, creatorId, nowIso) => {
    const sorted = [...new Set(participantIds)].sort();
    const state = get();
    const existing = Object.values(state.internalDmThreads).find((t) => {
      const a = [...t.participantIds].sort();
      return a.length === sorted.length && a.every((id, i) => id === sorted[i]);
    });
    if (existing) return existing.id;
    const id = `dm-thread-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const thread: InternalDmThread = {
      id,
      participantIds: sorted,
      createdAt: nowIso,
      messages: [],
    };
    set((s) => ({
      internalDmThreads: { ...s.internalDmThreads, [id]: thread },
    }));
    void creatorId;
    return id;
  },
  createChannel: (name, memberIds, creatorId, opts = {}) => {
    const id = `channel-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    // The creator is always a member so it appears in their own list immediately.
    const members = [...new Set([creatorId, ...memberIds])];
    const channel: InternalCommsChannel = {
      id,
      name: name.trim(),
      scope: 'network',
      memberIds: members,
      messages: [],
      ...(opts.description ? { description: opts.description } : {}),
      ...(opts.studioId ? { studioId: opts.studioId } : {}),
    };
    set((s) => ({
      internalComms: { ...s.internalComms, [id]: channel },
    }));
    return id;
  },
  addReaction: (scope, messageId, emoji, byPersonId) =>
    set((s) => {
      const applyToMessages = (msgs: InternalCommsMessage[]): InternalCommsMessage[] =>
        msgs.map((m) => {
          if (m.id !== messageId) return m;
          const existing = m.reactions ?? [];
          const idx = existing.findIndex(
            (r) => r.emoji === emoji && r.byPersonId === byPersonId,
          );
          const next = idx >= 0
            ? existing.filter((_, i) => i !== idx)
            : [...existing, { emoji, byPersonId }];
          return { ...m, reactions: next };
        });
      if (scope.kind === 'channel') {
        const ch = s.internalComms[scope.containerId];
        if (!ch) return s;
        return {
          internalComms: {
            ...s.internalComms,
            [scope.containerId]: { ...ch, messages: applyToMessages(ch.messages) },
          },
        };
      }
      const dm = s.internalDmThreads[scope.containerId];
      if (!dm) return s;
      return {
        internalDmThreads: {
          ...s.internalDmThreads,
          [scope.containerId]: { ...dm, messages: applyToMessages(dm.messages) },
        },
      };
    }),
  addAutoPing: (channelId, body, opts = {}) => {
    if (seedSilent()) return;
    set((s) => {
      const channel = s.internalComms[channelId];
      if (!channel) return s;
      const msg: InternalCommsMessage = {
        id: `ping-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        authorId: 'system-bot',
        body,
        timestamp: opts.timestamp ?? new Date().toISOString(),
        autoWinPing: true,
        customerRefId: opts.customerRefId,
        mentions: opts.mentions,
      };
      return {
        internalComms: {
          ...s.internalComms,
          [channelId]: { ...channel, messages: [...channel.messages, msg] },
        },
      };
    });
  },
  setRebookOutcome: (bookingId, outcome, attribution) =>
    set((s) => {
      const booking = s.bookings[bookingId];
      if (!booking) return s;
      return {
        bookings: {
          ...s.bookings,
          [bookingId]: { ...booking, rebookOutcome: outcome, rebookAttribution: attribution },
        },
      };
    }),
  upsertRebookFollowup: (task) =>
    set((s) => ({
      rebookFollowupTasks: { ...s.rebookFollowupTasks, [task.id]: task },
    })),
  upsertHealingCheck: (task) =>
    set((s) => ({
      healingCheckTasks: { ...s.healingCheckTasks, [task.id]: task },
    })),
  upsertNoRebookFlag: (flag) =>
    set((s) => ({
      noRebookFlags: { ...s.noRebookFlags, [flag.id]: flag },
    })),
  upsertNoDepositDeclineFlag: (flag) =>
    set((s) => ({
      noDepositDeclineFlags: { ...s.noDepositDeclineFlags, [flag.id]: flag },
    })),
  upsertOverrideRequest: (request) =>
    set((s) => ({
      overrideRequests: { ...s.overrideRequests, [request.id]: request },
    })),
  upsertSalePayment: (payment) =>
    set((s) => ({ salePayments: { ...s.salePayments, [payment.id]: payment } })),
  upsertPayoutRecord: (record) =>
    set((s) => ({ payoutRecords: { ...s.payoutRecords, [record.id]: record } })),
  upsertCashLedger: (ledger) =>
    set((s) => ({ cashLedgers: { ...s.cashLedgers, [ledger.id]: ledger } })),
  upsertMerchItem: (item) =>
    set((s) => ({ merchItems: { ...s.merchItems, [item.id]: item } })),
  upsertDayPack: (pack) =>
    set((s) => ({ dayPacks: { ...s.dayPacks, [pack.id]: pack } })),
  setSequenceState: (customerId, state) =>
    set((s) => ({
      sequenceState: { ...s.sequenceState, [customerId]: state },
    })),
  patchSequenceState: (customerId, patch) =>
    set((s) => {
      const existing = s.sequenceState[customerId] ?? { stage: 1 as const };
      return {
        sequenceState: {
          ...s.sequenceState,
          [customerId]: { ...existing, ...patch },
        },
      };
    }),
  clearSequenceState: (customerId) =>
    set((s) => {
      if (!(customerId in s.sequenceState)) return s;
      const next = { ...s.sequenceState };
      delete next[customerId];
      return { sequenceState: next };
    }),
  resolveRebookFollowup: (taskId, resolution) =>
    set((s) => {
      const task = s.rebookFollowupTasks[taskId];
      if (!task) return s;
      const updatedTask: RebookFollowupTask = {
        ...task,
        resolvedAt: resolution.resolvedAt,
        resolvedOutcome: resolution.outcome,
      };
      // Side-effect: patch the source booking's outcome so the leaderboard
      // and reporting view see the resolved state too.
      const source = s.bookings[task.sourceBookingId];
      const nextBookings = source && source.rebookOutcome?.kind === 'rebookDeferredToSales'
        ? {
            ...s.bookings,
            [task.sourceBookingId]: {
              ...source,
              rebookOutcome: {
                ...source.rebookOutcome,
                resolvedAt: resolution.resolvedAt,
                resolvedOutcome: resolution.outcome,
              },
            },
          }
        : s.bookings;
      return {
        rebookFollowupTasks: { ...s.rebookFollowupTasks, [taskId]: updatedTask },
        bookings: nextBookings,
      };
    }),
  reset: () =>
    set({
      initialised: false,
      customers: {},
      customerIdsByPhone: {},
      bookings: {},
      deposits: {},
      topupPlans: {},
      waivers: {},
      pieces: {},
      futureSignals: {},
      selfBookLinks: {},
      aiConversations: {},
      reactivationCampaigns: {},
      marketingCampaigns: {},
      confirmationCalls: {},
      internalComms: {},
      internalDmThreads: {},
      rebookFollowupTasks: {},
      healingCheckTasks: {},
      noRebookFlags: {},
      noDepositDeclineFlags: {},
      overrideRequests: {},
      reconResolutions: {},
      sequenceState: {},
      salePayments: {},
      payoutRecords: {},
      cashLedgers: {},
      merchItems: {},
      docketLines: {},
      settlementLandings: {},
      depositLandings: {},
      settlementLumps: {},
      dayPacks: {},
      studios: {},
      artists: {},
      agents: {},
      rolePermissions: DEFAULT_ROLE_PERMISSIONS,
      reportingTargets: DEFAULT_REPORTING_TARGETS,
      tailRecords: [],
      activityFeed: [],
      todayStats: { ...emptyStats },
    }),
}));

export type BrainStore = typeof useBrainStore;

// --- Cross-tab sync -------------------------------------------------------
// Mirror brain mutations across tabs over a BroadcastChannel so marking a
// booking completed on the floor tab reflects in the office tab (and back)
// without a server round-trip. No React involved — this is store ↔ store.
//
// A monotonic `revision` counter on each message is the feedback-loop guard:
// we only apply an incoming payload when its revision beats ours, and applying
// it never re-broadcasts (see `applyingRemote`).

// Versioned channel key. Bumped for the seed rebuild (real 11-studio network) so
// any tab still holding demo state keyed to the old fictional studio ids cannot
// broadcast it into a tab running the realigned seed.
const STORE_CHANNEL = 'addikted-to-ink-store-v2-seed';
const BROADCAST_THROTTLE_MS = 200;

interface StatePayload {
  type: 'state';
  revision: number;
  payload: Partial<BrainState>;
}

/**
 * The data slices worth broadcasting: everything except the action functions
 * (structured-clone can't serialise them) and `tailRecords` (a ~50k static seed
 * that never mutates and is loaded identically in every tab).
 */
function serialiseState(state: BrainState): Partial<BrainState> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(state)) {
    if (typeof value === 'function') continue;
    if (key === 'tailRecords') continue;
    out[key] = value;
  }
  return out as Partial<BrainState>;
}

if (typeof BroadcastChannel !== 'undefined') {
  const channel = new BroadcastChannel(STORE_CHANNEL);
  let revision = 0;
  let applyingRemote = false;
  let broadcastTimer: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    broadcastTimer = null;
    const message: StatePayload = {
      type: 'state',
      revision,
      payload: serialiseState(useBrainStore.getState()),
    };
    channel.postMessage(message);
  };

  useBrainStore.subscribe((state, prev) => {
    if (applyingRemote || state === prev) return;
    revision += 1;
    if (broadcastTimer === null) {
      broadcastTimer = setTimeout(flush, BROADCAST_THROTTLE_MS);
    }
  });

  channel.onmessage = (event: MessageEvent<StatePayload>) => {
    const message = event.data;
    if (!message || message.type !== 'state' || typeof message.revision !== 'number') return;
    if (message.revision <= revision) return; // stale or our own echo — ignore
    applyingRemote = true;
    try {
      useBrainStore.setState(message.payload); // merge: keeps local actions + tailRecords
      revision = message.revision;
    } finally {
      applyingRemote = false;
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      if (broadcastTimer !== null) clearTimeout(broadcastTimer);
      channel.close();
    });
  }
}
